import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { GradientButton } from '../gradients/GradientButton';
import { GradientTypography } from '../gradients/GradientTypography';
import { Link } from '../Link';
import { useTheme } from '@mui/material/styles';
import { Plan } from 'functions/src/types/firestore/Game';
import { TypographyParagraphed } from '../TypographyParagraphed';
import { useMobile } from 'src/hooks/useMobile';
import { ComingSoonText } from '../coming-soon/ComingSoonText';

// export type Plan = {
//   date: Date;
//   points: string[];
//   title?: string;
//   priority?: number;
// };
export interface RoadmapTimelineProps {
  nextPlans: Plan[];
  whitepaper: string;
  maxHeight?: number;
}

export function sortFn<T>(
  a: T,
  b: T,
  property: keyof T,
  isDescending?: boolean,
) {
  const order =
    a[String(property)] > b[String(property)]
      ? 1
      : a[String(property)] < b[String(property)]
      ? -1
      : 0;

  return isDescending ? order * -1 : order;
}

export function RoadmapTimeline({
  nextPlans,
  whitepaper,
  maxHeight,
}: RoadmapTimelineProps) {
  const theme = useTheme();
  const isMobile = useMobile();
  const timeline: Array<Plan> = nextPlans;

  !!timeline
    ? timeline.sort((a, b) => {
        return sortFn(a, b, 'priority', true);
      })
    : undefined;

  const gradient = `linear-gradient(${theme.palette.background.elevationSolid[10]}, 
    ${theme.palette.background.elevationSolid[10]}), 
    ${theme.palette.primary.vertical}`;

  return (
    <Box
      sx={{
        maxHeight,
      }}
    >
      {!isMobile && (
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-end"
          sx={{ ml: '12px', mb: 2 }}
        >
          <GradientTypography gradientColor="primary.vertical" variant="h5">
            ROADMAP
          </GradientTypography>
          {!!whitepaper && (
            <Link
              href={whitepaper}
              target="_blank"
              sx={{ textDecoration: 'none' }}
            >
              <GradientButton size="small">WHITEPAPER</GradientButton>
            </Link>
          )}
        </Stack>
      )}
      {!!nextPlans && nextPlans.length > 0 ? (
        <Box
          sx={{
            maxHeight: 'inherit',
            overflow: 'hidden',
            border: 'solid 2px transparent',
            borderRadius: '10px',
            backgroundImage: gradient,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            boxShadow: theme.shadows[4],
            '&:hover': {
              boxShadow: theme.shadows[7],
            },
          }}
        >
          <Box
            sx={{
              maxHeight: 'inherit',
              overflowY: 'auto',
            }}
          >
            <Timeline
              sx={{
                '.MuiTimelineItem-root': {
                  width: 'max-content',
                  maxWidth: '100%',
                  '&::before': {
                    content: 'unset',
                  },
                },
              }}
            >
              {timeline?.map(({ points, priority, title }, i) => {
                return (
                  <TimelineItem key={`tl-${priority?.toString()}`}>
                    <TimelineSeparator
                      sx={{
                        '&.MuiTimelineSeparator-root': {
                          '.MuiTimelineDot-root': {
                            width: '24px',
                            height: '24px',
                            background: theme.palette.primary.horizontal,
                            border: 'unset',
                            m: 'unset',
                            backgroundPosition: 'center',
                          },
                          '.MuiTimelineConnector-root': {
                            width: '1px',
                            background: theme.palette.primary.horizontal,
                          },
                        },
                      }}
                    >
                      <TimelineDot />
                      {timeline.length !== i + 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{
                        p: 'unset',
                        ml: 4,
                        mb: 8,
                        position: 'relative',
                        maxWidth: 'calc(100% - 44px)',
                        height: 'fit-content',
                        border: 'solid 1px transparent',
                        borderRadius: '4px',
                        backgroundImage: `linear-gradient(${theme.palette.background.elevationSolid[18]}, 
                        ${theme.palette.background.elevationSolid[18]}),
                        ${theme.palette.primary.vertical}`,
                        backgroundOrigin: 'border-box',
                        backgroundClip: 'content-box, border-box',
                        '&::before': {
                          content: "''",
                          background:
                            'linear-gradient(45deg, #4DC8FF 50%, rgba(0,100,233,0) 50%)',
                          width: 12,
                          height: 12,
                          top: 6,
                          left: -6,
                          position: 'absolute',
                          transform: 'rotate(45deg)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: '4px 8px',
                          width: '500px',
                          maxWidth: '100%',
                          boxShadow: theme.shadows[4],
                          '&:hover': {
                            boxShadow: theme.shadows[7],
                          },
                        }}
                      >
                        <Typography variant="h6">{title}</Typography>
                        <List
                          sx={{
                            p: 'unset',
                          }}
                        >
                          {points.map((point) => {
                            return (
                              <TypographyParagraphed
                                key={`plan-${point}`}
                                text={point}
                              />
                            );
                          })}
                        </List>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 40 }}>
          <ComingSoonText title={'Timeline'} />
        </Box>
      )}
    </Box>
  );
}
